import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { placementStyle, useAnchoredPosition } from './anchoredPosition'
import { useOverlayZIndex } from './overlayZIndex'
import { CHIP_POPOVER_CLOSE } from './layoutBuilderCopy'
import './layoutBuilder.css'

// The popover's CSS max-height (60vh) as a ratio, used to pick its side once from its largest size.
const POPOVER_MAX_VH = 0.6

// Rendered (so it can be measured) but invisible until its first placement, which happens before paint.
const HIDDEN_UNTIL_PLACED = { left: 0, top: 0, visibility: 'hidden' }

// Shared shell of the picker's detail popovers (flow-type chips, Visual Builder components). The
// owner keeps `open` (so only one is open at a time) and passes the trigger via `anchorRef`.
// Portaled to document.body with fixed positioning against the trigger, because the picker panels
// scroll (overflow:auto) and would clip an in-place popover. Closes on Escape, on any pointer-down
// outside the trigger and popover, and when the trigger scrolls out of view.
export default function ChipPopover ({ anchorRef, open, onClose, ariaLabel, title, children }) {
  const popoverRef = useRef(null)
  const position = useAnchoredPosition(open, anchorRef, popoverRef, { capRatio: POPOVER_MAX_VH, onAnchorHidden: onClose })
  useOverlayZIndex(open, popoverRef)

  useEffect(() => {
    if (!open) return
    // Capture phase + stopPropagation so Escape closes only the popover, not the enclosing dialog.
    const onKeyDown = event => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    // Capture phase on both pointerdown and mousedown, so a handler below (a card, the dialog or
    // its mask) that stops propagation can never swallow the dismissal.
    const onPointerDown = event => {
      if (anchorRef.current?.contains(event.target) || popoverRef.current?.contains(event.target)) return
      onClose()
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('mousedown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('mousedown', onPointerDown, true)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null
  return createPortal(
    // React bubbles portal events to the card; stop them so a click inside never selects it.
    <div
      ref={popoverRef}
      className='layoutBuilder-chip-popover'
      role='dialog'
      aria-label={ariaLabel}
      style={position ? placementStyle(position) : HIDDEN_UNTIL_PLACED}
      onClick={event => event.stopPropagation()}
    >
      <div className='layoutBuilder-chip-popover-header'>
        <strong>{title}</strong>
        <button type='button' className='layoutBuilder-chip-popover-close' aria-label={CHIP_POPOVER_CLOSE} onClick={onClose}>×</button>
      </div>
      {children}
    </div>,
    document.body
  )
}
